import CompositionBridge from "../CompositionBridge";
import EngineEventHandlers from "../../engine/EngineEventHandlers";
import { CompositionService } from "./CompositionService";
import { CompositionPersistenceService } from "./CompositionPersistenceService";
import { SceneThumbnailService } from "./SceneThumbnailService";
import { CompositionStateStore } from "../store/CompositionStateStore";
import { snapCanvasSizeToPreset } from "../../utils/canvasSizing";
import { ApplicationStore } from "../store/ApplicationStore";
import { ScriptLayerStore } from "../store/ScriptLayerStore";
import { TimelineStore } from "../store/TimelineStore";
import { ContextMenuStore } from "../store/ContextMenuStore";
import { ExportStore } from "../store/ExportStore";
import { UndoManager } from "./UndoManager";
import { CompositionRestoreService } from "./CompositionRestoreService";
import { LayoutService } from "./LayoutService";
import { APP_RELEASE_ID } from "../../config/appRelease";
import {
    ContributionCapture,
    buildCompositionStructureCheckpoint,
    type ContributionCheckpointDraftV1,
    type ContributionSessionEndReason,
} from "../../instrumentation/contribution";

export type ResolveAssetUrlFn = (
    assetId: string,
    type: "audio" | "video" | "image"
) => Promise<string | undefined>;

export type ResolveEnhancedAudioUrlFn = (trackId: string) => Promise<string | undefined>;

/**
 * Owns the lifecycle of the main editor Composition instance:
 * - Tear down old engine + listeners
 * - Clear MobX stores
 * - Init persistence for the new compositionId
 * - Restore from Firestore into a clean engine + store state
 */
export class CompositionSessionService {
    private static loadSeq = 0;
    private static captureStartInFlight: Promise<void> | null = null;
    private static captureStartKey: string | null = null;
    private static readonly RESTORE_LAYOUT_REAPPLY_DELAY_MS = 100;
    private static readonly RESTORE_LAYOUT_REAPPLY_MAX_ATTEMPTS = 30;

    static get sessionSeq(): number {
        return this.loadSeq;
    }

    private static async buildCaptureCheckpoint(
        compositionId: string,
    ): Promise<ContributionCheckpointDraftV1 | undefined> {
        try {
            return await buildCompositionStructureCheckpoint({
                compositionId,
                display: { ...CompositionStateStore.display },
                durationSec: CompositionStateStore.compositionDuration,
                isEmpty: CompositionStateStore.isCompositionEmpty,
                orderedLayerIds: [...CompositionStateStore.orderedLayerIds],
                layers: CompositionStateStore.layers,
                clips: CompositionStateStore.clips,
            });
        } catch {
            return undefined;
        }
    }

    static async closeContributionCaptureSession(reason: ContributionSessionEndReason): Promise<void> {
        try {
            await this.captureStartInFlight;
        } catch {}
        const context = ContributionCapture.getActiveSessionContext();
        if (!context) return;
        try {
            await UndoManager.flushContributionOperations();
        } catch {}
        const finalCheckpoint = await this.buildCaptureCheckpoint(context.compositionId);
        await ContributionCapture.endCompositionSession({ reason, finalCheckpoint });
    }

    /**
     * Starts capture for an already-restored composition. This is also used when
     * a server-managed grant arrives after the editor finished restoring.
     */
    static async ensureContributionCaptureSession(args: {
        workspaceId?: string;
        projectId: string;
        compositionId: string;
        workspaceRole?: string;
        readOnly: boolean;
        sessionSeq?: number;
    }): Promise<void> {
        const projectId = String(args.projectId ?? "").trim();
        const compositionId = String(args.compositionId ?? "").trim();
        if (!projectId || !compositionId) return;
        if (args.sessionSeq !== undefined && args.sessionSeq !== this.loadSeq) return;
        if (!ContributionCapture.isEnabledForProject(projectId)) return;

        const active = ContributionCapture.getActiveSessionContext();
        if (active?.projectId === projectId && active.compositionId === compositionId) return;

        const key = `${this.loadSeq}:${projectId}:${compositionId}`;
        if (this.captureStartInFlight && this.captureStartKey === key) {
            await this.captureStartInFlight;
            return;
        }

        const start = (async () => {
            const initialCheckpoint = await this.buildCaptureCheckpoint(compositionId);
            if (args.sessionSeq !== undefined && args.sessionSeq !== this.loadSeq) return;
            if (!ContributionCapture.isEnabledForProject(projectId)) return;
            await ContributionCapture.startCompositionSession({
                workspaceId: args.workspaceId,
                projectId,
                compositionId,
                role: args.workspaceRole,
                readOnly: Boolean(args.readOnly),
                appRelease: APP_RELEASE_ID,
            }, initialCheckpoint);
        })();
        this.captureStartInFlight = start;
        this.captureStartKey = key;
        try {
            await start;
        } finally {
            if (this.captureStartInFlight === start) {
                this.captureStartInFlight = null;
                this.captureStartKey = null;
            }
        }
    }

    private static async reapplySceneLayoutsAfterRestore(): Promise<void> {
        for (let attempt = 0; attempt < this.RESTORE_LAYOUT_REAPPLY_MAX_ATTEMPTS; attempt++) {
            // Abort if composition was unloaded/reloaded mid-restore.
            const currentSeq = this.loadSeq;
            try {
                const scriptClip: any = CompositionService.getScriptLayerClip?.();
                if (!scriptClip) return;

                const scenes: any[] = typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];
                const sources: any[] = typeof scriptClip.getVideoSources === "function" ? scriptClip.getVideoSources() : [];

                // Wait until the ScriptLayerClip has materialized its sources/scenes (async init).
                if (!Array.isArray(scenes) || scenes.length === 0 || !Array.isArray(sources) || sources.length === 0) {
                    await new Promise((r) => setTimeout(r, this.RESTORE_LAYOUT_REAPPLY_DELAY_MS));
                    if (currentSeq !== this.loadSeq) return;
                    continue;
                }

                for (const s of scenes) {
                    const sceneIndex = Number((s as any)?.index ?? NaN);
                    if (!Number.isFinite(sceneIndex) || sceneIndex < 0) continue;
                    const layout = scriptClip.getSceneLayout?.(sceneIndex) as string | null | undefined;
                    if (!layout) continue;
                    try {
                        LayoutService.applySceneLayout(sceneIndex, layout);
                    } catch {}
                }

                // Force a redraw while paused so restored layouts are visible immediately.
                try {
                    const comp: any = CompositionService.composition;
                    void comp?.recomputeFrameAtCurrentTimeAsync?.();
                } catch {}

                return;
            } catch {
                // If something is transiently unavailable during restore, retry briefly.
                await new Promise((r) => setTimeout(r, this.RESTORE_LAYOUT_REAPPLY_DELAY_MS));
                if (currentSeq !== this.loadSeq) return;
            }
        }
    }

    static async loadComposition(args: {
        userId: string;
        workspaceId?: string;
        compositionId: string;
        projectId?: string;
        workspaceRole?: string;
        resolveAssetUrl: ResolveAssetUrlFn;
        resolveEnhancedAudioUrl?: ResolveEnhancedAudioUrlFn;
        /**
         * If true, skip Firestore clip restoration and treat this session as an empty composition.
         * Useful for avoiding unnecessary restore churn (and UI flashes) when the composition is known-empty.
         */
        expectedEmpty?: boolean;
        readOnly?: boolean;
        /**
         * Optional display settings to apply immediately after engine init.
         * Lets the UI avoid an extra Firestore fetch for empty compositions.
         */
        displaySettings?: { width?: number; height?: number; backgroundColor?: string };
    }): Promise<void> {
        const mySeq = ++this.loadSeq;

        // Capture the previous composition before any stores or engine state are reset.
        // The recorder is fail-open, so capture failures never block editor teardown.
        try {
            await this.closeContributionCaptureSession("composition_switched");
        } catch {}

        // Invalidate any in-flight async work tied to previous compositions.
        CompositionService.bumpSessionVersion();
        try {
            SceneThumbnailService.teardown();
        } catch {}

        // Tear down listeners first so engine teardown doesn't re-populate stores.
        try {
            UndoManager.teardown();
        } catch {}
        try {
            CompositionBridge.teardown();
        } catch {}
        try {
            EngineEventHandlers.teardown();
        } catch {}

        // Block any persistence writes during teardown/reset.
        try {
            CompositionPersistenceService.setTearingDown(true);
        } catch {}

        // Reset all UI/editor stores to a known empty baseline.
        try {
            ExportStore.reset();
        } catch {}
        try {
            ContextMenuStore.reset();
        } catch {}
        try {
            TimelineStore.reset();
        } catch {}
        try {
            ApplicationStore.reset();
        } catch {}
        try {
            ScriptLayerStore.clear();
        } catch {}
        try {
            CompositionStateStore.reset();
        } catch {}

        // Destroy engine resources (audio ctx, renderer, tickers).
        try {
            await CompositionService.destroy();
        } catch {}

        // Ensure this request is still the latest.
        if (mySeq !== this.loadSeq) return;

        // Init a fresh engine composition.
        await CompositionService.initialize({
            force: true,
            compositionId: args.compositionId,
        });

        // Init persistence for the target compositionId.
        CompositionPersistenceService.reset();
        CompositionPersistenceService.init(args.userId, args.compositionId, args.workspaceId, args.readOnly);
        CompositionPersistenceService.setTearingDown(false);

        // Seed the thumbnail service with session resolvers (used to build detached compositions).
        try {
            SceneThumbnailService.init({
                compositionId: args.compositionId,
                resolveAssetUrl: args.resolveAssetUrl,
                resolveEnhancedAudioUrl: args.resolveEnhancedAudioUrl,
            });
        } catch {}

        // Wire listeners for store sync but skip persistence writes during restoration.
        CompositionBridge.setRestoring(true);
        CompositionBridge.init();
        EngineEventHandlers.init();

        // Apply display settings early when provided (primarily for empty compositions).
        if (args.displaySettings) {
            try {
                const comp = CompositionService.composition as any;
                const bg = args.displaySettings.backgroundColor;
                if (typeof bg === "string" && bg.length > 0) {
                    comp.changeBackground(bg);
                }
            } catch {}
            try {
                const comp = CompositionService.composition as any;
                const w = Number(args.displaySettings.width);
                const h = Number(args.displaySettings.height);
                if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
                    const snapped = snapCanvasSizeToPreset(w, h);
                    comp.resize(snapped.width, snapped.height);
                }
            } catch {}
        }

        if (!args.expectedEmpty) {
            // Restore saved state (or stay empty if none exists).
            try {
                await CompositionRestoreService.restoreFromFirestore({
                    compositionId: args.compositionId,
                    resolveAssetUrl: args.resolveAssetUrl,
                    resolveEnhancedAudioUrl: args.resolveEnhancedAudioUrl,
                });
            } catch (e) {
                console.error("[CompositionSessionService] restoreFromPersistence failed", e);
            }
        }

        // Ensure this request is still the latest.
        if (mySeq !== this.loadSeq) return;

        if (!args.expectedEmpty) {
            // Ensure explicit per-scene layouts are applied after restore (not only on canvas resize).
            // This avoids “layout selected but default placement shown” edge cases, e.g. after inserting
            // an empty scene before a layout-managed scene and reopening the composition.
            try {
                await this.reapplySceneLayoutsAfterRestore();
            } catch {}

            // Hydrate transcript words for the script layer clip, if present.
            try {
                await CompositionBridge.syncScriptLayerStore();
            } catch {}
        }

        // Enable persistence writes and undo recording after restoration.
        CompositionBridge.setRestoring(false);

        if (!args.readOnly) {
            UndoManager.init({ resolveAssetUrl: args.resolveAssetUrl });
        }

        if (mySeq !== this.loadSeq) return;
        try {
            await this.ensureContributionCaptureSession({
                workspaceId: args.workspaceId,
                projectId: String(args.projectId ?? ""),
                compositionId: args.compositionId,
                workspaceRole: args.workspaceRole,
                readOnly: Boolean(args.readOnly),
                sessionSeq: mySeq,
            });
        } catch {}
    }

    /**
     * Tear down the active editor session and clear all related UI/engine state.
     * Use when switching projects or leaving the editor.
     */
    static async unloadComposition(): Promise<void> {
        const mySeq = ++this.loadSeq;

        // Persist the final structure-only checkpoint before clearing editor state.
        try {
            await this.closeContributionCaptureSession("project_switched");
        } catch {}

        // Invalidate any in-flight async work tied to previous compositions.
        CompositionService.bumpSessionVersion();
        try {
            SceneThumbnailService.teardown();
        } catch {}

        // Tear down listeners first so engine teardown doesn't re-populate stores.
        try {
            UndoManager.teardown();
        } catch {}
        try {
            CompositionBridge.teardown();
        } catch {}
        try {
            EngineEventHandlers.teardown();
        } catch {}

        // Block any persistence writes during teardown/reset.
        try {
            CompositionPersistenceService.setTearingDown(true);
        } catch {}

        // Reset all UI/editor stores to a known empty baseline.
        try {
            ExportStore.reset();
        } catch {}
        try {
            ContextMenuStore.reset();
        } catch {}
        try {
            TimelineStore.reset();
        } catch {}
        try {
            ApplicationStore.reset();
        } catch {}
        try {
            ScriptLayerStore.clear();
        } catch {}
        try {
            CompositionStateStore.reset();
        } catch {}

        // Destroy engine resources (audio ctx, renderer, tickers).
        try {
            await CompositionService.destroy();
        } catch {}

        // Ensure this request is still the latest.
        if (mySeq !== this.loadSeq) return;

        // Fully reset persistence state.
        try {
            CompositionPersistenceService.reset();
        } catch {}
    }
}
